<script>
import axios from "axios";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Login ",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      username: "",
      password: "",
      error: "",
      loader: false,
      showPassword: false,
      forgotPasswordMode: false,
      forgotEmail: "",
      forgotLoader: false,
      forgotSuccess: false,
      forgotError: "",
    };
  },
  methods: {
    async login() {
      this.loader = true;
      let formData = new FormData();
      formData.append("username", this.username);
      formData.append("password", this.password);
      let url = "/login";
      await axios
        .post(url, formData)
        .then((response) => {
          if (response.status == 200) {
            let userData = response.data.user;
            let token = response.data.token;
            let schoolterm = response.data.schoolterm;
            let license = response.data.license;
            let next_term = response.data.next_term;
            axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
            localStorage.setItem("user", JSON.stringify(userData));
            localStorage.setItem("token", token);
            localStorage.setItem("schoolterm", schoolterm);
            localStorage.setItem("license", license);
            localStorage.setItem("next_term", next_term);

            const licenseDate = new Date(response.data.license);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            licenseDate.setHours(0, 0, 0, 0);

            if (licenseDate < today) {
              this.$router.push(
                this.$route.query.redirectFrom || { name: "license-expired" },
              );
            } else {
              this.$router.push(
                this.$route.query.redirectFrom || { name: "default" },
              );
            }
          } else if (response.status == 429) {
            this.error = response.data.message;
            this.loader = false;
          } else {
            this.error = response.data.message;
            this.loader = false;
          }
        })
        .catch(() => {
          this.loader = false;
        });
    },
    async submitForgotPassword() {
      this.forgotLoader = true;
      this.forgotError = "";
      try {
        await axios.post("/forgot-password", { email: this.forgotEmail });
        this.forgotSuccess = true;
      } catch (e) {
        this.forgotError =
          e?.response?.data?.message ||
          "Something went wrong. Please try again.";
      } finally {
        this.forgotLoader = false;
      }
    },
    togglePasswordVisibilty() {
      this.showPassword = !this.showPassword;
    },
    openForgotPassword() {
      this.forgotPasswordMode = true;
      this.forgotSuccess = false;
      this.forgotError = "";
      this.forgotEmail = "";
    },
    closeForgotPassword() {
      this.forgotPasswordMode = false;
    },
  },
  mounted() {
    document.body.classList.add("auth-body-bg");
  },
  computed: {
    inputType() {
      return this.showPassword ? "text" : "password";
    },
  },
};
</script>

<template>
  <div>
    <div class="container mt-5">
      <div class="row">
        <div class="col-md-10 col-lg-9 mx-auto">
          <div class="card shadow-lg border-0 overflow-hidden">
            <div class="card-body p-0">
              <div class="row g-0">
                <!-- Left Side: Form Section (Primary Color Background) -->
                <div class="col-md-6 form-section">
                  <div class="form-wrapper p-4 p-lg-5">
                    <!-- Logo and Header -->
                    <div class="d-flex align-items-center mb-4">
                      <img
                        src="@/assets/images/logo.png"
                        class="brand-logo rounded-2"
                        height="54px"
                        alt="Logo"
                      />
                      <div class="ms-3">
                        <h5 class="text-white mb-0 fw-bold">Welcome Back!</h5>
                        <p class="text-white-50 small mb-0">
                          {{
                            forgotPasswordMode
                              ? "Reset your password"
                              : "Sign in to continue"
                          }}
                        </p>
                      </div>
                    </div>

                    <!-- Divider -->
                    <div class="section-divider mb-4">
                      <span>{{
                        forgotPasswordMode ? "Reset Password" : "Sign In"
                      }}</span>
                    </div>

                    <!-- Error Alert -->
                    <div
                      v-if="error && !forgotPasswordMode"
                      class="alert-error mb-3"
                    >
                      <span class="mdi mdi-alert-circle-outline me-2"></span
                      >{{ error }}
                    </div>

                    <!-- LOGIN FORM -->
                    <div v-if="!forgotPasswordMode">
                      <b-form @submit.prevent="login">
                        <div class="form-field mb-3">
                          <label class="field-label text-white">Username</label>
                          <div class="input-wrapper">
                            <span
                              class="mdi mdi-account-outline input-icon"
                            ></span>
                            <b-form-input
                              v-model="username"
                              required
                              type="text"
                              placeholder="Enter your username"
                              class="styled-input"
                            ></b-form-input>
                          </div>
                        </div>

                        <div class="form-field mb-2">
                          <label class="field-label text-white">Password</label>
                          <div class="input-wrapper">
                            <span
                              class="mdi mdi-lock-outline input-icon"
                            ></span>
                            <b-form-input
                              v-model="password"
                              required
                              :type="inputType"
                              placeholder="Enter your password"
                              class="styled-input with-toggle"
                            ></b-form-input>
                            <button
                              class="toggle-btn"
                              type="button"
                              @click="togglePasswordVisibilty"
                            >
                              <span
                                :class="[
                                  'mdi',
                                  showPassword ? 'mdi-eye' : 'mdi-eye-off',
                                ]"
                              ></span>
                            </button>
                          </div>
                        </div>

                        <div
                          class="d-flex justify-content-between align-items-center mb-4"
                        >
                          <div class="form-check">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              id="remember-check"
                            />
                            <label
                              class="form-check-label text-white-50 small"
                              for="remember-check"
                            >
                              Remember me
                            </label>
                          </div>
                          <a
                            class="forgot-link"
                            href="#"
                            @click.prevent="openForgotPassword"
                          >
                            Forgot password?
                          </a>
                        </div>

                        <b-button
                          type="submit"
                          variant="light"
                          class="login-btn w-100"
                        >
                          <span v-if="!loader">
                            Sign In
                            <span class="mdi mdi-arrow-right ms-1"></span>
                          </span>
                          <b-spinner v-else small label="Loading..." />
                        </b-button>
                      </b-form>
                    </div>

                    <!-- FORGOT PASSWORD FORM -->
                    <div v-else>
                      <div v-if="!forgotSuccess">
                        <p class="text-white-50 small mb-3">
                          Enter the email address linked to your account and
                          we'll send you a reset link.
                        </p>

                        <div v-if="forgotError" class="alert-error mb-3">
                          <span class="mdi mdi-alert-circle-outline me-2"></span
                          >{{ forgotError }}
                        </div>

                        <b-form @submit.prevent="submitForgotPassword">
                          <div class="form-field mb-4">
                            <label class="field-label text-white"
                              >Email Address</label
                            >
                            <div class="input-wrapper">
                              <span
                                class="mdi mdi-email-outline input-icon"
                              ></span>
                              <b-form-input
                                v-model="forgotEmail"
                                required
                                type="email"
                                placeholder="Enter your email"
                                class="styled-input"
                              ></b-form-input>
                            </div>
                          </div>

                          <b-button
                            type="submit"
                            variant="light"
                            class="login-btn w-100 mb-3"
                          >
                            <span v-if="!forgotLoader">
                              Send Reset Link
                              <span class="mdi mdi-send ms-1"></span>
                            </span>
                            <b-spinner v-else small label="Sending..." />
                          </b-button>

                          <div class="text-center">
                            <a
                              class="forgot-link"
                              href="#"
                              @click.prevent="closeForgotPassword"
                            >
                              <span class="mdi mdi-arrow-left me-1"></span>Back
                              to Sign In
                            </a>
                          </div>
                        </b-form>
                      </div>

                      <div v-else class="success-box text-center">
                        <div class="success-icon mb-3">
                          <span class="mdi mdi-check-circle-outline"></span>
                        </div>
                        <h6 class="fw-bold text-white mb-2">
                          Check your email
                        </h6>
                        <p class="text-white-50 small mb-4">
                          A password reset link has been sent to
                          <strong>{{ forgotEmail }}</strong
                          >.
                        </p>
                        <a
                          class="forgot-link"
                          href="#"
                          @click.prevent="closeForgotPassword"
                        >
                          <span class="mdi mdi-arrow-left me-1"></span>Back to
                          Sign In
                        </a>
                      </div>
                    </div>

                    <!-- Footer -->
                    <p class="footer-text text-center mt-4">
                      a product of
                      <a
                        href="https://tandikatechnologies.com/"
                        class="footer-link"
                      >
                        Tandika Technologies Uganda Limited
                      </a>
                    </p>
                  </div>
                </div>

                <!-- Right Side: Image Section -->
                <div class="col-md-6 d-none d-md-block image-section">
                  <div class="image-overlay">
                    <img
                      src="@/assets/images/2.jpg"
                      class="side-image"
                      alt="School"
                    />
                    <!--<div class="image-caption">
                      <div class="caption-badge">
                        <span class="mdi mdi-school me-2"></span>Quality
                        Education
                      </div>
                      <h3 class="caption-title">
                        Empowering <br />Future Leaders
                      </h3>
                      <p class="caption-text">
                        A complete school management solution for modern
                        education
                      </p>
                    </div>-->
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Root Variables */
:root {
  --primary: #e53935;
  --primary-dark: #b71c1c;
  --radius: 10px;
  --shadow: 0 20px 35px -10px rgba(0, 0, 0, 0.15);
}

/* Card Styling */
.card {
  border-radius: 20px !important;
  background: transparent;
}

/* Left Form Section */
.form-section {
  background: linear-gradient(135deg, #e53935 0%, #b71c1c 100%);
  position: relative;
}

.form-wrapper {
  min-height: 500px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Brand Logo */
.brand-logo {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: white;
  padding: 4px;
}

/* Section Divider */
.section-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.25);
  }
}

/* Alert Error */
.alert-error {
  background: rgba(255, 255, 255, 0.95);
  border-left: 3px solid #ff6b6b;
  color: #c0392b;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  display: flex;
  align-items: center;
}

/* Form Fields */
.form-field {
  margin-bottom: 1rem;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  display: block;
  letter-spacing: 0.3px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  font-size: 16px;
  color: #adb5bd;
  z-index: 2;
  pointer-events: none;
}

.styled-input {
  padding-left: 38px !important;
  border-radius: var(--radius) !important;
  border: none !important;
  background: rgba(255, 255, 255, 0.95) !important;
  font-size: 14px !important;
  height: 44px !important;
  transition: all 0.2s !important;
  width: 100%;

  &:focus {
    background: white !important;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.4) !important;
    outline: none !important;
  }

  &.with-toggle {
    padding-right: 44px !important;
  }
}

/* Placeholder Color */
.styled-input::placeholder {
  color: #adb5bd;
  font-size: 13px;
}

/* Toggle Password Button */
.toggle-btn {
  position: absolute;
  right: 0;
  height: 44px;
  width: 44px;
  background: transparent;
  border: none;
  color: #adb5bd;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  z-index: 2;
  transition: color 0.2s;
  border-radius: 0 var(--radius) var(--radius) 0;

  &:hover {
    color: #e53935;
  }
}

/* Forgot Link */
.forgot-link {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    color: white;
    text-decoration: underline;
  }
}

/* Login Button (Light variant on dark background) */
.login-btn {
  height: 44px;
  font-weight: 700;
  font-size: 14px;
  border-radius: var(--radius) !important;
  background: white !important;
  color: #e53935 !important;
  border: none !important;
  letter-spacing: 0.5px;
  transition: all 0.2s !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2) !important;
    background: #f8f9fa !important;
  }

  &:active {
    transform: translateY(0);
  }
}

/* Success Box */
.success-box {
  padding: 1rem 0;
}

.success-icon {
  font-size: 56px;
  color: #2ecc71;
  line-height: 1;
}

/* Footer Text */
.footer-text {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0;
  padding-top: 1.5rem;
}

.footer-link {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    color: white;
    text-decoration: underline;
  }
}

/* Right Image Section */
.image-section {
  position: relative;
  overflow: hidden;
  background: #1a1a2e;
}

.image-overlay {
  position: relative;
  height: 100%;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.side-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.image-overlay::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(229, 57, 53, 0.3) 0%,
    rgba(0, 0, 0, 0.5) 100%
  );
  z-index: 1;
}

.image-caption {
  position: relative;
  z-index: 2;
  padding: 2rem;
  color: white;
  text-align: center;
  max-width: 80%;
}

.caption-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  border-radius: 50px;
  padding: 5px 14px;
  font-size: 11px;
  font-weight: 500;
  margin-bottom: 1rem;
}

.caption-title {
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 0.75rem;
}

.caption-text {
  font-size: 13px;
  opacity: 0.85;
  margin-bottom: 0;
}

/* Form Check Custom Styling */
::v-deep .form-check-input {
  background-color: rgba(255, 255, 255, 0.2);
  border: none;
  cursor: pointer;

  &:checked {
    background-color: white;
    border-color: white;
  }

  &:focus {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
}

::v-deep .form-check-label {
  cursor: pointer;
  font-size: 12px;
}

/* Responsive Adjustments */
@media (max-width: 767px) {
  .form-wrapper {
    padding: 2rem !important;
  }

  .card {
    margin: 1rem;
  }
}

/* Carousel Overrides (for compatibility) */
::v-deep .carousel-caption {
  position: relative !important;
  right: 0;
  bottom: 1.25rem;
  left: 0;
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
  color: #495057;
  text-align: center;
}

::v-deep .carousel-indicators li {
  background-color: rgba(229, 57, 53, 0.25);
}
</style>
